import { useSchedulerStore } from '@/stores/schedulerStore.js';
import { ScheduleCard } from '@/components/scheduler/ScheduleCard.js';
import { ScrollArea } from '@/components/ui/scroll-area.js';
import { Calendar } from 'lucide-react';
import { LoadingState } from '@/components/common/LoadingState.js';

interface SchedulesListProps {
    searchQuery: string;
    onViewDetails?: (scheduleId: string) => void;
}

export function SchedulesList({ searchQuery, onViewDetails }: SchedulesListProps) {
    const { schedules, runningScheduleIds, isLoading } = useSchedulerStore();

    // Filter schedules by search
    const allSchedules = Array.from(schedules.values());
    const filteredSchedules = searchQuery
        ? allSchedules.filter(
              (s) =>
                  s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  s.task.instruction.toLowerCase().includes(searchQuery.toLowerCase())
          )
        : allSchedules;

    // Sort: running first, then enabled by next run, then disabled
    const sortedSchedules = [...filteredSchedules].sort((a, b) => {
        const aRunning = runningScheduleIds.has(a.id);
        const bRunning = runningScheduleIds.has(b.id);
        if (aRunning !== bRunning) return aRunning ? -1 : 1;

        if (a.enabled !== b.enabled) return a.enabled ? -1 : 1;

        if (a.enabled && b.enabled) {
            return (a.nextRunAt || 0) - (b.nextRunAt || 0);
        }

        return b.updatedAt - a.updatedAt;
    });

    // Show loading state
    if (isLoading) {
        return <LoadingState message="Loading routines..." />;
    }

    if (sortedSchedules.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center h-full p-8 text-center">
                <Calendar className="h-12 w-12 text-muted-foreground mb-4 opacity-50" />
                {searchQuery ? (
                    <>
                        <p className="text-sm text-muted-foreground mb-2">No routines found</p>
                        <p className="text-xs text-muted-foreground/70">
                            Try a different search term
                        </p>
                    </>
                ) : (
                    <>
                        <p className="text-sm text-muted-foreground mb-2">No routines yet</p>
                        <p className="text-xs text-muted-foreground/70 mb-4">
                            Ask me to set up recurring tasks for you
                        </p>
                        <p className="text-xs text-muted-foreground/70 italic">
                            "Remind me to check emails every morning at 9am"
                        </p>
                    </>
                )}
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full">
            {/* Header */}
            <div className="px-3 py-2 border-b border-border/50">
                <span className="text-xs text-muted-foreground">
                    {sortedSchedules.length} routine{sortedSchedules.length !== 1 ? 's' : ''}
                </span>
            </div>

            {/* List */}
            <ScrollArea className="flex-1">
                <div className="flex flex-col gap-2 p-2">
                    {sortedSchedules.map((schedule) => (
                        <ScheduleCard
                            key={schedule.id}
                            schedule={schedule}
                            isRunning={runningScheduleIds.has(schedule.id)}
                            onViewDetails={onViewDetails}
                        />
                    ))}
                </div>
            </ScrollArea>
        </div>
    );
}
